import type { ReceiptTransactionData } from '../types/receipt'

interface TransactionResultModalProps {
  isOpen: boolean
  onClose: () => void
  onReprint: () => void
  // 등록 완료된 거래 데이터 (입력 폼 상태가 아닌 실제 등록된 값 — #5 QA 피드백)
  transactionData: ReceiptTransactionData | null
  errorMessage?: string
}

const formatAmount = (value: string) => {
  const n = parseFloat(value)
  return Number.isNaN(n) ? value : n.toLocaleString('ko-KR')
}

function TransactionResultModal({
  isOpen,
  onClose,
  onReprint,
  transactionData,
  errorMessage,
}: TransactionResultModalProps) {
  if (!isOpen || !transactionData) return null

  const { selectedCurrency, foreignAmount, krwAmount, rate, tradeType, lines, totalKRW } = transactionData
  const isMulti = !!lines && lines.length > 1

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-[480px] max-w-[90vw] flex flex-col">
        {/* 헤더 */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-fx-text-dark text-center">
            {tradeType === 'buy' ? '매입 완료' : '매각 완료'}
          </h2>
        </div>

        {/* 거래 결과 */}
        <div className="p-6 space-y-4">
          {isMulti ? (
            // 다중 통화: 통화별 리스트 + 총액
            <div className="bg-gray-50 rounded-lg p-4 space-y-3 max-h-64 overflow-y-auto">
              {lines.map(line => (
                <div key={line.currency} className="flex justify-between items-center pb-2 border-b border-gray-200 last:border-0 last:pb-0">
                  <div>
                    <span className="text-lg font-bold text-fx-text-dark">{line.currency}</span>
                    <span className="ml-2 text-sm text-fx-text-muted">환율 {formatAmount(line.rate)}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-fx-text-dark">
                      {formatAmount(line.foreignAmount)} {line.currency}
                    </div>
                    <div className="text-sm text-fx-text-muted">{formatAmount(line.krwAmount)} 원</div>
                  </div>
                </div>
              ))}
              <div className="border-t-2 border-gray-300 pt-3 flex justify-between items-center">
                <span className="text-fx-text-muted font-medium">
                  {tradeType === 'buy' ? '지급 총액' : '수취 총액'}
                </span>
                <span className="text-2xl font-bold text-fx-buy-dark">
                  {formatAmount(totalKRW ?? krwAmount)} 원
                </span>
              </div>
            </div>
          ) : (
            // 단일 통화
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-fx-text-muted">외화</span>
                <span className="text-xl font-bold text-fx-text-dark">
                  {formatAmount(foreignAmount)} {selectedCurrency}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-fx-text-muted">적용 환율</span>
                <span className="text-lg text-fx-text-dark">
                  {formatAmount(rate)}
                </span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                <span className="text-fx-text-muted">원화</span>
                <span className="text-2xl font-bold text-fx-buy-dark">
                  {formatAmount(krwAmount)} 원
                </span>
              </div>
            </div>
          )}

          {/* 에러 메시지 (영수증 발행 실패) */}
          {errorMessage && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 p-4 rounded-lg">
              <p className="font-medium">영수증 발행 실패</p>
              <p className="text-sm mt-1">{errorMessage}</p>
            </div>
          )}
        </div>

        {/* 버튼 영역 */}
        <div className="p-6 border-t border-gray-200 flex gap-3">
          <button
            onClick={onReprint}
            className="flex-1 h-14 text-lg font-bold bg-blue-500 text-white rounded-lg hover:opacity-90 active:opacity-80 transition-all"
          >
            재출력
          </button>
          <button
            onClick={onClose}
            className="flex-1 h-14 text-lg font-bold bg-gray-500 text-white rounded-lg hover:opacity-90 active:opacity-80 transition-all"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  )
}

export default TransactionResultModal
